import React, { useEffect, useState } from "react";
import {
  Button,
  CircularProgress,
  TextField,
  Typography
} from "@material-ui/core";
import api from "../../services/api";

const money = value =>
  Number(value).toLocaleString("pt-BR", {
    style: "currency",
    currency: "BRL"
  });

const getMonths = planValue => {
  try {
    const plan = JSON.parse(planValue || "{}");
    return Number(plan.billingMonths || 1);
  } catch {
    return 0;
  }
};

export default function CouponQuotePreview({
  couponCode,
  onCouponChange,
  invoiceId,
  planValue
}) {
  const [quote, setQuote] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const billingMonths = getMonths(planValue);
  const code = couponCode.trim().toUpperCase();

  useEffect(() => {
    setQuote(null);
    setError("");
  }, [code, billingMonths, invoiceId]);

  const checkCoupon = async () => {
    setQuote(null);
    setError("");

    if (!/^[A-Z0-9_-]{3,64}$/.test(code)) {
      setError("Informe um cupom válido.");
      return;
    }

    if (![1, 6, 12].includes(billingMonths)) {
      setError("Selecione um período de pagamento válido.");
      return;
    }

    setLoading(true);

    try {
      const { data } = await api.post("/coupons/quote", {
        invoiceId,
        billingMonths,
        couponCode: code
      });

      if (
        data.previewOnly !== true ||
        Number(data.invoiceId) !== Number(invoiceId) ||
        Number(data.billingMonths) !== billingMonths ||
        data.code !== code ||
        !Number.isFinite(Number(data.total)) ||
        Number(data.total) <= 0 ||
        !Number.isFinite(Number(data.planTotal)) ||
        Number(data.planTotal) <= 0 ||
        !Number.isFinite(Number(data.couponSavings)) ||
        Number(data.couponSavings) <= 0
      ) {
        throw new Error("Cotação inválida.");
      }

      setQuote({
        ...data,
        requestedCode: code,
        requestedMonths: billingMonths
      });
    } catch (err) {
      setError(
        err.response?.data?.error ||
        err.response?.data?.message ||
        "Não foi possível validar o cupom."
      );
    } finally {
      setLoading(false);
    }
  };

  const currentQuote =
    quote &&
    quote.requestedCode === code &&
    quote.requestedMonths === billingMonths
      ? quote
      : null;

  return (
    <div style={{ marginTop: 16, textAlign: "left" }}>
      <TextField
        fullWidth
        margin="normal"
        variant="outlined"
        label="Cupom de desconto (opcional)"
        value={couponCode}
        onChange={event =>
          onCouponChange(
            event.target.value
              .toUpperCase()
              .replace(/[^A-Z0-9_-]/g, "")
              .slice(0, 64)
          )
        }
        inputProps={{ maxLength: 64 }}
        helperText="Consulte o cupom para conferir o valor."
      />

      <Button
        variant="outlined"
        color="primary"
        size="small"
        onClick={checkCoupon}
        disabled={loading || code.length < 3}
      >
        {loading ? (
          <CircularProgress size={18} />
        ) : (
          "Consultar desconto"
        )}
      </Button>

      {error && (
        <Typography
          color="error"
          variant="body2"
          style={{ marginTop: 10 }}
        >
          {error}
        </Typography>
      )}

      {currentQuote && (
        <div style={{ marginTop: 14 }}>
          <Typography variant="body2">
            Valor sem cupom: {money(currentQuote.planTotal)}
          </Typography>

          <Typography variant="body2">
            Economia com cupom: {money(currentQuote.couponSavings)}
          </Typography>

          <Typography variant="h6">
            Total com cupom: {money(currentQuote.total)}
          </Typography>

          <Typography variant="body2">
            Meses com desconto: {currentQuote.couponMonthsApplied}
          </Typography>

          <Typography
            variant="caption"
            display="block"
            style={{ marginTop: 8 }}
          >
            Prévia informativa. O cupom será validado e reservado
            novamente ao gerar a cobrança; a disponibilidade
            e o valor final serão confirmados pelo servidor.
          </Typography>
        </div>
      )}
    </div>
  );
}
